import { formatReportDate } from "./estimateReport";
import { filterVisibleHeaderFields } from "./reportHeader";
import { computeEstimateTaxSummary } from "./estimateTax";
import { formatInvoiceStatus, parseInvoiceTaxLines } from "./invoices";
import { roundCurrency } from "./invoiceReview";
import {
  buildInvoiceReportSections,
  getActiveInvoiceLineItems,
  INVOICE_REPORT_SECTION_DEFAULT_OPTIONS,
} from "./invoiceReportSections";
import {
  buildPaymentInstructionsPresentation,
  resolveInvoicePaymentInstructions,
} from "./invoicePaymentInstructions";
import {
  buildPaymentTermsPresentation,
  resolveInvoicePaymentTermsFields,
} from "./invoicePaymentTerms";
import {
  buildCustomerNotesPresentation,
  resolveInvoiceCustomerNotes,
} from "./invoiceCustomerNotes";
import {
  buildInvoiceFooterPresentation,
  resolveInvoiceFooter,
} from "./invoiceFooter";

export {
  getApplicableInvoiceReportSections,
  getPresentInvoiceLineTypes,
  INVOICE_REPORT_SECTIONS,
} from "./invoiceReportSections";

export const DEFAULT_INVOICE_REPORT_OPTIONS = Object.freeze({
  ...INVOICE_REPORT_SECTION_DEFAULT_OPTIONS,
  showTaxes: true,
  showGrandTotal: true,
  showInvoiceNumber: true,
  showInvoiceDate: true,
  showBillingPeriod: false,
  showSourceEstimate: false,
  showClientReference: false,
  billingPeriodMode: "entire_project",
  billingPeriodStart: "",
  billingPeriodEnd: "",
});

export function billingPeriodOptionsFromInvoice(invoice) {
  const start = normalizeBillingDateValue(invoice?.billing_period_start);
  const end = normalizeBillingDateValue(invoice?.billing_period_end);

  if (start && end) {
    return {
      billingPeriodMode: "custom_range",
      billingPeriodStart: start,
      billingPeriodEnd: end,
    };
  }

  return {
    billingPeriodMode: "entire_project",
    billingPeriodStart: "",
    billingPeriodEnd: "",
  };
}

function normalizeBillingDateValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function mergeInvoiceReportOptions(options = {}) {
  return { ...DEFAULT_INVOICE_REPORT_OPTIONS, ...options };
}

function visibleSectionSubtotal(section) {
  if (!section || section.mode === "hidden") return 0;
  return Number(section.subtotal || 0);
}

/**
 * Export invoice report as a PDF file (pdfmake + platform-aware delivery).
 */
export { downloadInvoiceReportPdf as exportInvoiceReportPdf } from "../pdf/invoice/downloadInvoicePdf";

/**
 * Legacy browser print export (deprecated).
 * Prints InvoiceReportPrintDocument (off-screen), not the on-screen preview.
 * Disconnected from UI — retained until legacy print stack is removed.
 */
export function exportInvoiceReportToPdf() {
  document.body.classList.add("estimate-report-printing");
  window.print();
  window.setTimeout(() => {
    document.body.classList.remove("estimate-report-printing");
  }, 1000);
}

function formatBillingPeriodLabel(invoice) {
  const start = normalizeBillingDateValue(invoice?.billing_period_start);
  const end = normalizeBillingDateValue(invoice?.billing_period_end);

  if (start && end) {
    return `${formatReportDate(start)} – ${formatReportDate(end)}`;
  }

  if (start) {
    return `From ${formatReportDate(start)}`;
  }

  if (end) {
    return `Through ${formatReportDate(end)}`;
  }

  return "All project work";
}

function nonEmptyText(value) {
  const text = String(value || "").trim();
  return text || null;
}

function formatClientAddress(client) {
  if (!client || typeof client !== "object") return null;

  const lines = [];
  const street = nonEmptyText(client.address);
  if (street) lines.push(street);

  const locality = [client.city, client.province, client.postal_code]
    .map((part) => nonEmptyText(part))
    .filter(Boolean)
    .join(", ");
  if (locality) lines.push(locality);

  const country = nonEmptyText(client.country);
  if (country) lines.push(country);

  return lines.length > 0 ? lines.join(", ") : null;
}

function resolveInvoiceProjectName(invoice) {
  return (
    nonEmptyText(invoice?.projects?.project_name) ||
    nonEmptyText(invoice?.title) ||
    null
  );
}

function buildHeaderPresentation(invoice, options) {
  const sourceEstimateNumber =
    nonEmptyText(invoice.source_estimate_number) ||
    nonEmptyText(invoice.estimates?.estimate_number) ||
    null;
  const client = invoice.clients || {};
  const clientName =
    nonEmptyText(client.client_name) || nonEmptyText(invoice.client_name);
  const projectName = resolveInvoiceProjectName(invoice);
  const projectNumber = nonEmptyText(invoice.projects?.project_number);
  const siteAddress = nonEmptyText(invoice.projects?.site_address);
  const projectContact = nonEmptyText(invoice.projects?.client_contact_name);
  const invoiceNumber = nonEmptyText(invoice.invoice_number);

  const fields = filterVisibleHeaderFields([
    {
      key: "client",
      label: "Client",
      value: clientName,
      show: Boolean(clientName),
    },
    {
      key: "clientContact",
      label: "Contact",
      value: nonEmptyText(client.contact_name),
      show: true,
    },
    {
      key: "clientAddress",
      label: "Address",
      value: formatClientAddress(client),
      show: true,
    },
    {
      key: "clientEmail",
      label: "Email",
      value: nonEmptyText(client.email),
      show: true,
    },
    {
      key: "clientPhone",
      label: "Phone",
      value: nonEmptyText(client.phone),
      show: true,
    },
    {
      key: "project",
      label: "Project",
      value: projectName,
      show: Boolean(projectName),
    },
    {
      key: "projectNumber",
      label: "Project #",
      value: projectNumber,
      show: true,
    },
    {
      key: "siteAddress",
      label: "Site",
      value: siteAddress,
      show: true,
    },
    {
      key: "projectContact",
      label: "Project Contact",
      value: projectContact,
      show: true,
    },
    {
      key: "invoiceNumber",
      label: "Invoice #",
      value: invoiceNumber,
      show: options.showInvoiceNumber && Boolean(invoiceNumber),
    },
    {
      key: "invoiceDate",
      label: "Date",
      value: formatReportDate(invoice.created_at || new Date()),
      show: options.showInvoiceDate,
    },
    {
      key: "status",
      label: "Status",
      value: formatInvoiceStatus(invoice.status),
      show: true,
    },
    {
      key: "sourceEstimate",
      label: "Source Estimate",
      value: sourceEstimateNumber,
      show: options.showSourceEstimate && Boolean(sourceEstimateNumber),
    },
    {
      key: "clientReference",
      label: "Client Reference",
      value: nonEmptyText(invoice.client_reference),
      show: options.showClientReference,
    },
    {
      key: "billingPeriod",
      label: "Billing Period",
      value: formatBillingPeriodLabel(invoice),
      show: options.showBillingPeriod,
    },
  ]);

  return {
    invoiceNumber: invoiceNumber || "—",
    invoiceId: invoice.id ?? null,
    clientName: clientName || "—",
    projectName: projectName || "—",
    statusLabel: formatInvoiceStatus(invoice.status),
    generatedDate: formatReportDate(invoice.created_at || new Date()),
    billingPeriodLabel: formatBillingPeriodLabel(invoice),
    sourceEstimateNumber,
    clientReference: nonEmptyText(invoice.client_reference),
    fields,
  };
}

/**
 * Build a structured invoice report model from snapshotted invoice data + options.
 */
export function buildInvoiceReportModel(invoice, options = {}) {
  if (!invoice) return null;

  const mergedOptions = mergeInvoiceReportOptions(options);
  const lineItems = getActiveInvoiceLineItems(invoice.lineItems || []);
  const { sections, sectionsOrdered } = buildInvoiceReportSections(
    lineItems,
    mergedOptions,
  );

  // Report totals are derived only from visible sections so Preview/PDF
  // always reconcile with rendered line items (hidden sections excluded).
  const subtotalLabour = visibleSectionSubtotal(sections.labour);
  const subtotalMaterials = visibleSectionSubtotal(sections.materials);
  const subtotalServices = visibleSectionSubtotal(sections.services);
  const lineSubtotal = roundCurrency(
    sectionsOrdered.reduce(
      (sum, entry) => sum + visibleSectionSubtotal(entry.section),
      0,
    ),
  );
  const projectDiscountPercent = Number(invoice.project_discount_percent || 0);
  const goodwillAdjustment = Number(invoice.goodwill_adjustment || 0);
  const afterProjectDiscount = roundCurrency(
    lineSubtotal * (1 - projectDiscountPercent / 100),
  );
  const subtotal = roundCurrency(afterProjectDiscount + goodwillAdjustment);
  const taxLines = parseInvoiceTaxLines(invoice);
  const taxSummary = computeEstimateTaxSummary(subtotal, taxLines);
  const grandTotal = Number(taxSummary.grandTotal);

  const reportTaxSummary =
    mergedOptions.showTaxes && taxSummary.hasTax ? taxSummary : null;

  return {
    generatedAt: new Date(),
    options: {
      ...mergedOptions,
      totalLabel: "Grand Total",
    },
    header: buildHeaderPresentation(invoice, mergedOptions),
    sections,
    sectionsOrdered,
    summary: {
      labourTotal: subtotalLabour,
      materialsTotal: subtotalMaterials,
      servicesTotal: subtotalServices,
      lineSubtotal,
      projectDiscountPercent,
      goodwillAdjustment,
      afterProjectDiscount,
      subtotal,
      taxSummary: reportTaxSummary,
      grandTotal,
    },
    taxSummary: reportTaxSummary,
    grandTotal: mergedOptions.showGrandTotal ? grandTotal : null,
    paymentInstructions: buildPaymentInstructionsPresentation(
      resolveEffectivePaymentInstructions(invoice, mergedOptions),
    ),
    paymentTerms: buildPaymentTermsPresentation(
      resolveEffectivePaymentTermsInvoice(invoice, mergedOptions),
    ),
    customerNotes: buildCustomerNotesPresentation(
      resolveEffectiveCustomerNotes(invoice, mergedOptions),
    ),
    invoiceFooter: buildInvoiceFooterPresentation(
      resolveEffectiveInvoiceFooter(invoice, mergedOptions),
    ),
    // Legacy alias — prefer customerNotes.title + notes text.
    notes: resolveEffectiveCustomerNotes(invoice, mergedOptions) || null,
  };
}

function resolveEffectivePaymentInstructions(invoice, options) {
  const status = String(invoice?.status || "").toLowerCase();
  if (status && status !== "draft") {
    return invoice.payment_instructions;
  }

  return resolveInvoicePaymentInstructions({
    invoice,
    organizationPaymentInstructions:
      options.organizationPaymentInstructions ?? null,
  });
}

function resolveEffectivePaymentTermsInvoice(invoice, options) {
  const status = String(invoice?.status || "").toLowerCase();
  if (status && status !== "draft") {
    return invoice;
  }

  if (invoice?.payment_terms_override === true) {
    return invoice;
  }

  const orgTerms = options.organizationPaymentTerms;
  if (!orgTerms) {
    return invoice;
  }

  const state = resolveInvoicePaymentTermsFields({
    invoice,
    organizationPaymentTerms: orgTerms,
  });

  return {
    ...invoice,
    payment_terms_preset: state.payment_terms_preset,
    payment_terms_days: state.payment_terms_days,
    invoice_date: state.invoice_date,
    due_date: state.due_date,
  };
}

function resolveEffectiveCustomerNotes(invoice, options) {
  const status = String(invoice?.status || "").toLowerCase();
  if (status && status !== "draft") {
    return invoice?.notes?.trim() || null;
  }

  return (
    resolveInvoiceCustomerNotes({
      invoice,
      organizationCustomerNotes: options.organizationCustomerNotes ?? null,
    }) || null
  );
}

function resolveEffectiveInvoiceFooter(invoice, options) {
  const status = String(invoice?.status || "").toLowerCase();
  if (status && status !== "draft") {
    return invoice?.invoice_footer?.trim() || null;
  }

  return (
    resolveInvoiceFooter({
      invoice,
      organizationInvoiceFooter: options.organizationInvoiceFooter ?? null,
    }) || null
  );
}

export function getInvoiceTotalPresentation(reportModel) {
  if (!reportModel?.options?.showGrandTotal || reportModel.grandTotal == null) {
    return null;
  }

  const taxSummary = reportModel.taxSummary;
  const summary = reportModel.summary || {};

  if (taxSummary?.hasTax) {
    return {
      mode: "withTax",
      lineSubtotal: summary.lineSubtotal,
      projectDiscountPercent: summary.projectDiscountPercent,
      goodwillAdjustment: summary.goodwillAdjustment,
      afterProjectDiscount: summary.afterProjectDiscount,
      subtotal: taxSummary.subtotal,
      lines: taxSummary.lines,
      total: taxSummary.grandTotal,
      totalLabel: "Grand Total",
    };
  }

  return {
    mode: "simple",
    total: reportModel.grandTotal,
    totalLabel: reportModel.options?.totalLabel || "Grand Total",
  };
}
